import { readQueryParam, setQueryParam } from '../../../utils/component-query-param'

export interface TargetZoneDeepLinkElement extends HTMLElement {
	dataZoneId: string
}

type RootNode = ReturnType<HTMLElement['getRootNode']>

export interface TargetZoneChangeDetail {
	zoneID: string
	targetZoneID: string
	/** When true, listeners should not write this change back to the URL. */
	silent?: boolean
	/**
	 * Root node (document or shadow root) the change originated from. Zones in a different root ignore
	 * the event, so the same zone id can be reused across roots (e.g. global nav vs page).
	 */
	sourceRoot?: RootNode
}

export function getSectionIds(targetZone: TargetZoneDeepLinkElement): string[] {
	return Array.from(targetZone.querySelectorAll<HTMLElement>('.gb-zone-section'))
		.map(section => section.dataset.zoneSectionId)
		.filter((sectionId): sectionId is string => !!sectionId)
}

/**
 * Returns the section id authored as the default zone, if any.
 */
export function getDefaultSectionId(targetZone: TargetZoneDeepLinkElement): string | null {
	const defaultSection = targetZone.querySelector<HTMLElement>('.gb-zone-section[data-is-default-zone="true"]')
	return defaultSection?.dataset.zoneSectionId ?? null
}

export function isDeepLinkEnabled(targetZone: TargetZoneDeepLinkElement): boolean {
	return !!targetZone.dataZoneId && getSectionIds(targetZone).length > 0
}

export function dispatchTargetZoneChange(
	zoneId: string,
	sectionId: string,
	silent = false,
	sourceRoot?: RootNode
): void {
	window.dispatchEvent(
		new CustomEvent<TargetZoneChangeDetail>('targetZoneChange', {
			bubbles: true,
			cancelable: false,
			detail: {
				zoneID: zoneId,
				targetZoneID: sectionId,
				silent,
				sourceRoot,
			},
		})
	)
}

export function applyDeepLinkFromUrl(targetZone: TargetZoneDeepLinkElement): void {
	if (!isDeepLinkEnabled(targetZone)) {
		return
	}

	const sectionIds = getSectionIds(targetZone)
	const urlSectionId = readQueryParam(targetZone.dataZoneId)
	let targetSectionId: string | null = null
	if (urlSectionId && sectionIds.includes(urlSectionId)) {
		targetSectionId = urlSectionId
	} else if (!urlSectionId) {
		targetSectionId = getDefaultSectionId(targetZone)
	}

	if (!targetSectionId) {
		return
	}

	// silent=true → listeners won't echo this back to the URL.
	dispatchTargetZoneChange(targetZone.dataZoneId, targetSectionId, true, targetZone.getRootNode())
}

export function syncUrlFromSelection(targetZone: TargetZoneDeepLinkElement, sectionId: string): void {
	if (!isDeepLinkEnabled(targetZone)) {
		return
	}

	const sectionIds = getSectionIds(targetZone)
	if (!sectionIds.includes(sectionId)) {
		return
	}

	const currentValue = readQueryParam(targetZone.dataZoneId)
	if (currentValue === sectionId) {
		return
	}

	setQueryParam(targetZone.dataZoneId, sectionId)
}
